import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import Select from 'react-select';
import Swal from 'sweetalert2';
import FinOpsOrganizationView from "./FinOpsOrganizationView";
import FinOpsAccountView from "./FinOpsAccountView";
import FinOpsOrganizationListView from "./FinOpsOrganizationListView";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const API_URL = "https://49pgt66j8g.execute-api.us-west-2.amazonaws.com/prod";
const TENANT_API_URL = "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod";

const FinOpsDashboard = ({ isSidebarOpen }) => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [loadingText, setLoadingText] = useState('Loading FinOps...');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  
  // View mode: 'organizations' | 'accounts' | 'account-detail'
  const [viewMode, setViewMode] = useState('accounts');
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      try {
        const tenantID = localStorage.getItem('tenantID');
        const organizationID = localStorage.getItem('organizationID');
        
        if (!tenantID && !organizationID) {
          setLoading(false);
          return;
        }

        let response;
        if (tenantID) {
          response = await fetch(`${TENANT_API_URL}/v2/tenant/settings?tenantID=${tenantID}`);
        } else {
          const apiDomain = process.env.REACT_APP_API_DOMAIN || 'https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod';
          response = await fetch(`${apiDomain}/getUser`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ organizationID }),
          });
        }

        if (!response.ok) throw new Error('Failed to fetch accounts');

        const userData = await response.json();
        if (userData.cloudAccounts) {
          const cloudAccountsData = typeof userData.cloudAccounts === 'string' ? JSON.parse(userData.cloudAccounts) : userData.cloudAccounts;
          const accounts = [];
          const orgs = cloudAccountsData.organizations || [];
          const standaloneAccounts = cloudAccountsData.aws || [];
          
          // Detect view mode based on structure
          const hasMultipleOrgs = orgs.length > 1;
          const hasMixedAccounts = orgs.length > 0 && standaloneAccounts.length > 0;
          
          if (hasMultipleOrgs || hasMixedAccounts) {
            setViewMode('organizations');
            setOrganizations(orgs);
          } else if (orgs.length === 1) {
            setViewMode('accounts');
            setSelectedOrgId(orgs[0].orgId);
          } else {
            setViewMode('accounts');
            setSelectedOrgId(null); // No org, just standalone accounts
          }
          
          // Add standalone AWS accounts (not part of any org)
          if (standaloneAccounts && Array.isArray(standaloneAccounts)) {
            standaloneAccounts.forEach(acc => {
              accounts.push({
                id: acc.id,
                alias: acc.alias || acc.id,
                iamrole: acc.iamrole || '',
                orgId: null, // Standalone account
                isManagement: false,
                isStandalone: true
              });
            });
          }
          
          // Add organization accounts
          if (orgs && Array.isArray(orgs)) {
            orgs.forEach(org => {
              // Add management account
              if (org.id) {
                accounts.push({
                  id: org.id,
                  alias: org.alias ? `${org.alias} (Management)` : 'Management Account',
                  iamrole: org.managementRoleArn || '',
                  orgId: org.orgId,
                  isManagement: true,
                  isStandalone: false
                });
              }
              
              // Add member accounts
              if (org.memberAccounts && Array.isArray(org.memberAccounts)) {
                org.memberAccounts.forEach(member => {
                  if (member.connected) {
                    accounts.push({
                      id: member.accountId,
                      alias: member.accountName || member.accountId,
                      iamrole: member.memberRoleArn || '',
                      orgId: org.orgId,
                      isManagement: false,
                      isStandalone: false
                    });
                  }
                });
              }
            });
          }
          
          setConnectedAccounts(accounts);
          if (accounts.length > 0) {
            fetchDashboardData();
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching connected accounts:', error);
        setConnectedAccounts([]);
        setLoading(false);
      }
    };
    
    fetchConnectedAccounts();
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const tenantID = localStorage.getItem('tenantID');
        const organizationID = localStorage.getItem('organizationID');
        
        if (!tenantID && !organizationID) return;
        
        let response;
        if (tenantID) {
          response = await fetch(`${TENANT_API_URL}/v2/tenant/settings?tenantID=${tenantID}`);
        } else {
          const apiDomain = process.env.REACT_APP_API_DOMAIN || 'https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod';
          response = await fetch(`${apiDomain}/getUser`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ organizationID }),
          });
        }
        
        if (!response.ok) throw new Error('Failed to fetch accounts');
        
        const userData = await response.json();
        if (userData.cloudAccounts) {
          const cloudAccountsData = typeof userData.cloudAccounts === 'string' ? JSON.parse(userData.cloudAccounts) : userData.cloudAccounts;
          const accounts = [];
          
          // Add standalone AWS accounts
          if (cloudAccountsData.aws && Array.isArray(cloudAccountsData.aws)) {
            accounts.push(...cloudAccountsData.aws.map(account => ({
              alias: account.alias || account.id || '',
              accountId: account.accountId || (account.iamrole ? account.iamrole.split(':')[4] : 'Unknown') || 'Unknown',
              description: account.alias || account.id || '',
              iamrole: account.iamrole || '',
              orgId: null,
              isStandalone: true
            })));
          }
          
          // Add organization accounts
          if (cloudAccountsData.organizations && Array.isArray(cloudAccountsData.organizations)) {
            cloudAccountsData.organizations.forEach(org => {
              // Add management account
              if (org.id) {
                accounts.push({
                  alias: org.alias ? `${org.alias} (Management)` : 'Management Account',
                  accountId: org.id,
                  description: org.alias || 'Management Account',
                  iamrole: org.managementRoleArn || '',
                  orgId: org.orgId,
                  isManagement: true,
                  isStandalone: false
                });
              }
              
              // Add member accounts
              if (org.memberAccounts && Array.isArray(org.memberAccounts)) {
                org.memberAccounts.forEach(member => {
                  if (member.connected) {
                    accounts.push({
                      alias: member.accountName || member.accountId,
                      accountId: member.accountId,
                      description: member.accountName || member.accountId,
                      iamrole: member.memberRoleArn || '',
                      orgId: org.orgId,
                      isManagement: false,
                      isStandalone: false
                    });
                  }
                });
              }
            });
          }
          
          setCloudAccounts(accounts);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    fetchAccounts();
  }, []);
  
  const fetchDashboardData = async (accountId = null, orgId = null, month = null) => {
    try {
      const tenantID = localStorage.getItem("tenantID");
      const organizationID = localStorage.getItem("organizationID");
      const companyID = localStorage.getItem("companyID");
      const userType = localStorage.getItem("userType");
      
      if (!tenantID && !organizationID) {
        setLoading(false);
        return;
      }
      
      const queryMonth = month || selectedMonth;
      
      let url;
      if (tenantID) {
        // BACKWARD COMPATIBLE: Support both tenant and company users
        if (userType === 'company' && companyID) {
          url = `${TENANT_API_URL}/v2/finops/dashboard?companyID=${companyID}`;
        } else {
          url = `${TENANT_API_URL}/v2/finops/dashboard?tenantID=${tenantID}`;
        }
        if (orgId) url += `&orgId=${orgId}`;
        if (accountId) url += `&accountId=${accountId}`;
        if (queryMonth) url += `&month=${queryMonth}`;
      } else {
        url = `${API_URL}/dashboard?organizationID=${organizationID}`;
        if (orgId) url += `&orgId=${orgId}`;
        if (accountId) url += `&accountId=${accountId}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      setDashboardData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  const handleOrgSelect = async (orgId) => {
    setSelectedOrgId(orgId);
    setViewMode('accounts');
    setLoading(true);
    await fetchDashboardData(null, orgId, selectedMonth);
  };

  const handleAccountSelect = async (accountId) => {
    setSelectedAccountId(accountId);
    setViewMode('account-detail');
    setLoading(true);
    await fetchDashboardData(accountId, selectedOrgId, selectedMonth);
  };

  const handleBackToOrganizations = () => {
    setSelectedOrgId(null);
    setViewMode('organizations');
  };

  const handleBackToAccounts = () => {
    setSelectedAccountId(null);
    setViewMode('accounts');
    setLoading(true);
    fetchDashboardData(null, selectedOrgId, selectedMonth);
  };
  
  const getAccountsForOrg = (orgId) => {
    if (!orgId) {
      // Return standalone accounts only (not part of any org)
      return cloudAccounts.filter(acc => acc.isStandalone === true || acc.orgId === null);
    }
    // Return accounts for specific org
    return cloudAccounts.filter(acc => acc.orgId === orgId);
  };

  const handleScan = async () => {
    const tenantID = localStorage.getItem('tenantID');
    const organizationID = localStorage.getItem('organizationID');
    const companyID = localStorage.getItem('companyID');
    const userType = localStorage.getItem('userType');

    if (!tenantID && !organizationID) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'User ID is missing. Please log in again.' });
      return;
    }

    const selectedAccountData = cloudAccounts.find(
      account => account.accountId === selectedAccount?.value
    );

    if (!selectedAccountData?.iamrole) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'IAM role not found for selected account.' });
      return;
    }

    setIsScanning(true);

    try {
      let url, body;
      if (tenantID) {
        url = `${TENANT_API_URL}/v2/finops/scan/trigger`;
        // BACKWARD COMPATIBLE: Support both tenant and company users
        if (userType === 'company' && companyID) {
          body = { companyID };
        } else {
          body = { tenantID };
        }
      } else {
        url = `${API_URL}/scan/trigger`;
        body = { organizationID, accountId: selectedAccount.value };
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        Swal.fire({ icon: 'error', title: 'Scan Failed', text: errorData.message || 'Unknown error occurred.' });
        return;
      }

      Swal.fire({ icon: 'success', title: 'Success', text: 'Cost scan initiated successfully!' });
      setIsModalOpen(false);
      setTimeout(() => fetchDashboardData(), 3000);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'An unexpected error occurred while starting the scan.' });
    } finally {
      setIsScanning(false);
    }
  };



  // Rotating loading messages for better UX
  useEffect(() => {
    if (loading) {
      const isHistorical = selectedMonth !== `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      const messages = isHistorical ? [
        'Fetching historical data...',
        'Loading past month data...',
        'Retrieving historical costs...'
      ] : [
        'Loading FinOps...',
        'Fetching cost data...',
        'Analyzing spending patterns...',
        'Retrieving billing insights...',
        'Preparing your cost dashboard...'
      ];
      let index = 0;
      setLoadingText(messages[0]);
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingText(messages[index]);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [loading, selectedMonth]);

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
      </div>
    );
  }

  if (!connectedAccounts || connectedAccounts.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{
        backgroundImage: 'url("/images/blurred_image.png")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}>
        <div className="text-center text-dark p-4" style={{ backgroundColor: "rgba(170, 172, 242, 0.33)", borderRadius: "8px" }}>
          <h3>No Connected Accounts Found</h3>
          <p>To access this dashboard, please connect at least one account.</p>
          <button className="btn text-white mt-3" style={{backgroundColor : '#342ead'}}
            onClick={() => window.location.href = "/dashboard/connected-accounts"}>
            Connect Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cloudmap-page container-fluid pb-5" style={{ marginLeft: isSidebarOpen ? "0px" : "0px", transition: "margin-left 0.3s ease" }}>
      <style>{`
        .cloudmap-page .btn-scan {
          background-color: #342ead;
          color: white;
          border: none;
          padding: 8px 24px;
          border-radius: 6px;
          font-weight: 500;
        }
        .cloudmap-page .btn-scan:hover {
          background-color: #2a2390;
        }
        .cloudmap-page .react-select__control {
          border-radius: 6px;
        }
      `}</style>
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>FinOps</h2>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 0 }}>
              {localStorage.getItem('userType') === 'company' && (
                <span className="badge bg-info me-2">Company View</span>
              )}
              {selectedAccountId 
                ? 'Detailed cost analysis and waste resources for individual account'
                : 'Cost optimization and financial management across all AWS accounts'
              }
            </p>
          </div>
          <div className="d-flex gap-2">
            <input 
              type="month" 
              className="form-control" 
              style={{ width: '160px' }}
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setLoading(true);
                fetchDashboardData(selectedAccountId, selectedOrgId, e.target.value);
              }}
            />
            <button 
              className="btn btn-scan" 
              onClick={async () => {
                const tenantID = localStorage.getItem('tenantID');
                const companyID = localStorage.getItem('companyID');
                const userType = localStorage.getItem('userType');
                
                if (!tenantID) {
                  Swal.fire({ icon: 'error', title: 'Error', text: 'Tenant ID not found' });
                  return;
                }
                try {
                  // BACKWARD COMPATIBLE: Support both tenant and company users
                  const body = userType === 'company' && companyID 
                    ? { companyID } 
                    : { tenantID };
                  
                  const response = await fetch('https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/scan/trigger', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                  });
                  if (!response.ok) throw new Error('Failed to trigger scan');
                  Swal.fire({ icon: 'success', title: 'Scan Initiated', text: 'Tenant scan started successfully. This may take a few minutes depending on the number of accounts and regions.' });
                  setTimeout(() => fetchDashboardData(), 3000);
                } catch (err) {
                  Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to trigger scan: ' + err.message });
                }
              }}
            >
              Scan
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        {(viewMode === 'accounts' && organizations.length > 0) && (
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => { e.preventDefault(); handleBackToOrganizations(); }}>Organizations</a>
              </li>
              <li className="breadcrumb-item active">
                {selectedOrgId || 'Standalone Accounts'}
              </li>
            </ol>
          </nav>
        )}
        {viewMode === 'account-detail' && (
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb">
              {organizations.length > 0 && (
                <li className="breadcrumb-item">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleBackToOrganizations(); }}>Organizations</a>
                </li>
              )}
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => { e.preventDefault(); handleBackToAccounts(); }}>
                  {selectedOrgId || 'Accounts'}
                </a>
              </li>
              <li className="breadcrumb-item active">{selectedAccountId}</li>
            </ol>
          </nav>
        )}

        {/* View Rendering */}
        {viewMode === 'organizations' && (
          <FinOpsOrganizationListView
            organizations={organizations}
            standaloneAccountCount={connectedAccounts.filter(acc => acc.isStandalone).length}
            onOrgSelect={handleOrgSelect}
            selectedMonth={selectedMonth}
          />
        )}

        {viewMode === 'accounts' && (
          <FinOpsOrganizationView
            dashboardData={dashboardData}
            cloudAccounts={getAccountsForOrg(selectedOrgId)}
            onAccountSelect={handleAccountSelect}
            selectedMonth={selectedMonth}
          />
        )}

        {viewMode === 'account-detail' && (
          <FinOpsAccountView
            dashboardData={dashboardData}
            accountId={selectedAccountId}
            cloudAccounts={cloudAccounts}
            onBack={handleBackToAccounts}
          />
        )}
      </div>

      {isModalOpen && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title">Start Scan</h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)}></button>
              </div>
              <div className="modal-body py-5">
                <Select
                  className="react-select mb-3"
                  classNamePrefix="react-select"
                  options={[
                    { value: 'AWS', label: 'AWS' },
                    { value: 'GCP', label: 'GCP' },
                    { value: 'AZURE', label: 'AZURE' },
                  ]}
                  value={selectedProvider}
                  onChange={(selected) => {
                    setSelectedProvider(selected);
                    setSelectedAccount(null);
                  }}
                  placeholder="Select Cloud Provider"
                />
                <Select
                  className="react-select"
                  classNamePrefix="react-select"
                  options={
                    selectedProvider?.value === 'AWS'
                      ? cloudAccounts.map(account => ({
                          value: account.accountId,
                          label: (account.alias || account.description) ?
                            `${account.alias || account.description} (${account.accountId})` :
                            account.accountId,
                          alias: account.alias || account.description
                        }))
                      : []
                  }
                  value={selectedAccount}
                  onChange={(selected) => setSelectedAccount(selected)}
                  placeholder="Select Account"
                  isDisabled={!selectedProvider || selectedProvider.value !== 'AWS'}
                />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-scan" onClick={handleScan} disabled={isScanning || !selectedAccount}>
                  {isScanning ? 'Scanning...' : 'Scan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinOpsDashboard;

